<template>
  <div class="mb-0" v-resize="onResize" ref="mainDiv">
    <v-card class="my-0 mx-2" flat>
      <span class="d-flex mx-4 my-4">
        <v-btn color="primary" class="white--text mx-0 my-2" @click="navigateBack()">
              <v-icon large>mdi-chevron-left</v-icon>
        </v-btn>
        <v-spacer />
        <div class="mt-3">
          <span :class="smallerScreen ? 'text-h6' : 'text-h5'">
            {{groupName}}
          </span>
        </div>
        <v-spacer />
      </span>
      <v-card-text class="px-1">
        <v-row>
          <v-col cols="2" class="mt-3">
            <v-btn
              v-if="smallerScreen"
              :disabled="captureButtonDisabled"
              @click="startStepper()"
              class="white--text mt-1 mb-1" color="primary">
              <v-icon>mdi-camera</v-icon>
            </v-btn>
            <v-btn
              v-if="!smallerScreen"
              :disabled="captureButtonDisabled"
              @click="startStepper()"
              class="white--text" color="primary">
              Capture
            </v-btn>
          </v-col>
          <v-col cols="10" class="pr-0">
            <div class="d-flex">
              <!-- add button -->
              <v-btn fab :small="smallerScreen"
                v-if= "!cameraConnected"
                :class="smallerScreen ? 'white--text mt-3 mb-1' : 'white--text'" color="primary"
                @click="connectCamera()">
                <v-icon>mdi-power-plug</v-icon>
              </v-btn>

              <v-btn fab :small="smallerScreen"
                v-if="cameraConnected"
                :class="smallerScreen ? 'white--text mt-3 mb-1' : 'white--text'" color="primary"
                @click="disconnectCamera()">
                <v-icon>mdi-power-plug-off</v-icon>
              </v-btn>

              <!-- stream start button -->
              <v-btn :class="smallerScreen ? 'white--text mt-3 mb-1 ml-2' : 'white--text ml-2'" fab
                :small="smallerScreen" color="green" :disabled="cameraStreaming || !cameraConnected"
                @click="startStreaming()">
                <v-icon>mdi-play</v-icon>
              </v-btn>

              <!-- stream stop button -->
              <v-btn :class="smallerScreen ? 'white--text mt-3 mb-1 ml-2' : 'white--text ml-2'" fab
                :small="smallerScreen" color="red" :disabled="!cameraStreaming"
                @click="stopStreaming()">
                <v-icon>mdi-stop</v-icon>
              </v-btn>

              <!-- calibrate button -->
              <v-btn :class="smallerScreen ? 'white--text mt-3 mb-1 ml-2' : 'white--text ml-2'" fab
                :small="smallerScreen" color="red" :disabled="!cameraStreaming"
                @click="calibrate()">
                <v-icon>mdi-screwdriver</v-icon>
              </v-btn>

              <v-label
                v-if="cameraMessage.length > 0"
                :class="smallerScreen ? 'red--text mt-6 mb-1 ml-2' : 'red--text mt-4 ml-2'" fab
                :small="smallerScreen">
                {{ cameraMessage }}
              </v-label>
              <v-spacer/>
              <div class="mt-4" position-absolute right-0>
                <v-chip small v-if="visionGroup.operator" :class="smallerScreen ? 'mt-2 mb-1' : ''">
                  <v-icon small class="mr-1">mdi-account</v-icon>
                  {{ visionGroup.operator }}
                </v-chip>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row
          v-if="cameraStreaming">
            <v-col align="center">
              <div>
                <v-btn
                  v-if="colorVideo"
                  fab
                  small
                  @click="streamTypeTogle">
                    <v-icon>mdi-checkerboard</v-icon>
                </v-btn>
                <v-btn
                  v-else
                  fab
                  small
                  @click="streamTypeTogle">
                    <v-icon>mdi-palette-outline</v-icon>
                </v-btn>
              </div>
              <video
                v-if="colorVideo"
                ref="video"
                muted
                autoplay
                playsinline
                height="540px"
                width="960px"
                :srcObject.prop="colorStream"
              >             
              </video>
              <video
                v-else
                ref="video"
                autoplay
                playsinline
                muted
                height="540px"
                width="960px"
                :srcObject.prop="depthStream"
              >
              </video>           
            </v-col>
          <!-- <v-col align="center">
            <v-img src="@/assets/img/sample_color.png" alt=""
                contain
                height="540px"
                width="960px">
            </v-img>
          </v-col> -->
        </v-row>
        <v-row justify="center" class="mt-0" cols="12">
          <!--Insert the warninng label here with a condition that hides it-->
          <v-alert v-if="warningMsgDisplay" elevation="10" dismissible type="warning">
            {{ warningMsg }}
          </v-alert>
        </v-row>
        <v-card class="mt-5">
          <v-data-table 
            :height="tableHeight" 
            mobile-breakpoint="300" 
            disable-sort 
            dense
            :headers="tblHeaders" 
            :items="currentTrays" 
            class="elevation-1 pt-1 cardbackground"
            :single-select="true" 
            @focusin:row="clearSelectedItem" 
            @mouseout:row="clearSelectedItem"
            @click:row="selectItem" item-key="date" 
            :footer-props="{
              itemsPerPageOptions: smallerScreen ? [5, 10, 25] : [10, 25, 50],
              'xxdisable-items-per-page': smallerScreen ? true : false,
              itemsPerPageText: smallerScreen ? 'rows' : 'rows per page'
            }">
            <template v-slot:[`item.image1`] ="{ item }">
              <div align="center">
              <v-img class="max-width:160px; max-height:90px;"
                contain
                height="90px"
                width="90px"
                :src="item.sampleImage1"
                >
              </v-img>
              </div>
            </template>
            <template v-slot:[`item.image2`] ="{ item }">
              <div align="center">
              <v-img
                contain
                height="90px"
                width="90px"
                :src="item.sampleImage2"
                >
              </v-img>
              </div>
            </template>
            <template v-slot:[`item.date`]="{ item }">
              <pre class="ma-0 pa-0 caption">{{ formatDate(item.date) }}</pre>
            </template>
            <template v-slot:[`item.edit`]="{ item }">
               <v-btn fab small color="success" class="mx-1 my-1" @click="editTrayEntry(item)" :disabled="!cameraStreaming">
                <v-icon>mdi-pencil-outline</v-icon>
              </v-btn>
            </template>
            <template v-slot:[`item.delete`]="{ item }">
              <v-btn fab small color="secondary" class="mx-1 my-1" @click="confirmDeleteTrayEntry(item)">
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>

    <v-dialog v-model="dialogValues.showAddDialog" fullscreen>
			<v-stepper v-model="dialogValues.step" style="min-height:100%">
				<v-stepper-header>
					<v-stepper-step
          step="1"
          :complete="dialogValues.step > 1"
          >Information</v-stepper-step>
					<v-stepper-step
          step="2"
          :complete="dialogValues.step > 2"
          >Image 1</v-stepper-step>
          <v-stepper-step
          step="3"
          :complete="dialogValues.step > 3"
          >Image 2</v-stepper-step>
					<v-stepper-step
          step="4"
          >Review</v-stepper-step>
				</v-stepper-header>
				<v-stepper-items>
					<v-stepper-content step="1" class="position-relative fill-height" >
						<v-card flat>
							<div class="d-flex text-h5 ml-1 mb-2">
                <v-btn class="ml-2 white--text" @click="cancelEntryChanges()" color="red" fab>
									<v-icon>
                    mdi-close
                  </v-icon>
								</v-btn>
                <v-spacer />
                <div class="d-flex justify-content-center">
									Tray - Information
                </div>
								<v-spacer />
							</div>
              <v-divider/>
              <v-row>
							<v-col cols="2" class="text-body-1 d-flex flex-wrap" max-width="100%">
								<div class="ml-1 mb-3">Farm:
									<span class="text-primary">
										<b>{{ visionGroup.farm }}</b>
									</span>
								</div>
							</v-col>
              <v-col cols="2" class="text-body-1 d-flex flex-wrap" max-width="100%">
                <div class="ml-1 mb-3">Line: 
                  <span class="text-primary">
                    <b>{{ visionGroup.line }}</b>
                  </span>
                </div>
              </v-col>
              <v-col cols="2" class="text-body-1 d-flex flex-wrap" max-width="100%">
                <div class="ml-1 mb-3">Source MG:
                  <span class="text-primary">
                    <b>{{ visionGroup.mg }}</b>
                  </span>
                </div>
              </v-col>
              <v-col cols="2" class="text-body-1 d-flex flex-wrap" max-width="100%">
                <div v-if="this.dialogValues.editMode" class="text-primary">
                  <b>** EDIT **</b>
                </div>
              </v-col>
              <v-spacer/>
              <v-switch :style="breakpoint === 'xs' ? 'width: 100%;' : 'width: 25%;'"
                color="secondary" v-model="manualTrayOverride" :label="manualTrayOverride ? 'Manual Override' : 'Manual Override'" />
              </v-row>
              <!-- <div class="text-body-1 d-flex flex-wrap" max-width="100%">
                <span class="ml-1 mb-3">House:</span>
                <span class="text-primary">
                  {{ visionGroup.house }}
                </span>
              </div> -->
              <v-row>
                <v-spacer/>
                <v-col cols="4">
                  <v-text-field
                    v-if="manualTrayOverride"
                    v-model="dialogValues.stepperData.tray"
                    :disabled="dialogValues.editMode"
                    label="Tray"
                  />
                  <v-autocomplete
                    v-else
                    ref="trayRef"
                    v-model="dialogValues.stepperData.tray"
                    :disabled ="dialogValues.editMode"
                    label="Tray"
                    :items="trays"
                    auto-select-first
                    @input="confirmTrayValue()"
                    />
                </v-col>
                   <!-- :rules="[rules.required]" --> 
                <v-spacer/>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field 
                  v-model="dialogValues.stepperData.badge2"
                  label="Badge 2"
                  :disabled="!manualTrayOverride"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                  v-model="dialogValues.stepperData.badge3"
                  label="Badge 3"
                  :disabled="!manualTrayOverride"
                  />
                </v-col>
                <v-col>
                  <v-text-field 
                  v-model="dialogValues.stepperData.badge4"
                  label="Badge 4"
                  :disabled="!manualTrayOverride"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                  v-model="dialogValues.stepperData.badge5"
                  label="Badge 5"
                  :disabled="!manualTrayOverride"
                  />
                </v-col>
                <v-col>
                  <v-text-field
                  v-model="dialogValues.stepperData.badge6"
                  label="Badge 6"
                  :disabled="!manualTrayOverride"
                  />
                </v-col>
              </v-row>
						</v-card>
					</v-stepper-content>
					<v-stepper-content step="2">
						<v-card flat>
							<div class="d-flex align-center text-h5 mb-3 h-4">
                <v-btn class="ml-2 white--text" @click="cancelEntryChanges()" color="red" fab>
									<v-icon>
                    mdi-close
                  </v-icon>
								</v-btn>
                <v-spacer/>
									Image 1 - Above Light
								<v-spacer />
							</div>
              <v-divider/>
              <v-row class="ml-2">
                <v-col align="center" v-if="dialogValues.stepperData.sampleImage1===null">
                  <div>
                    <v-btn
                      v-if="colorVideo"
                      fab
                      small
                      @click="streamTypeTogle">
                        <v-icon>mdi-checkerboard</v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      fab
                      small
                      @click="streamTypeTogle">
                        <v-icon>mdi-palette-outline</v-icon>
                    </v-btn>
                  </div>
                  <video
                    v-if="colorVideo"
                    ref="video"
                    muted
                    autoplay
                    playsinline
                    height="540px"
                    width="960px"
                    :srcObject.prop="colorStream"
                  >             
                  </video>
                  <video
                    v-else
                    ref="video"
                    autoplay
                    playsinline
                    muted
                    height="540px"
                    width="960px"
                    :srcObject.prop="depthStream"
                  >
                  </video>                
                </v-col>
                <v-col align="center" v-else>
                  <v-img :src="dialogValues.stepperData.sampleImage1" alt=""
                      contain
                      height="540px"
                      width="960px">
                  </v-img>
                </v-col>
              </v-row>
            </v-card>
					</v-stepper-content>
					<v-stepper-content step="3">
						<v-card flat>
							<div class="d-flex align-center text-h5 mb-3 h-4">
                <v-btn class="ml-2 white--text" @click="cancelEntryChanges()" color="red" fab>
									<v-icon>
                    mdi-close
                  </v-icon>
								</v-btn>
                <v-spacer/>
									Image 2 - Below Light
								<v-spacer />
							</div>
              <v-divider/>
              <v-row class="ml-2">
                <v-col align="center" v-if="dialogValues.stepperData.sampleImage2 === null">
                  <div>
                    <v-btn
                      v-if="colorVideo"
                      fab
                      small
                      @click="streamTypeTogle">
                        <v-icon>mdi-checkerboard</v-icon>
                    </v-btn>
                    <v-btn
                      v-else
                      fab
                      small
                      @click="streamTypeTogle">
                        <v-icon>mdi-palette-outline</v-icon>
                    </v-btn>
                  </div>
                  <video
                    v-if="colorVideo"
                    ref="video"
                    muted
                    autoplay
                    playsinline
                    height="540px"
                    width="960px"
                    :srcObject.prop="colorStream"
                  >             
                  </video>
                  <video
                    v-else
                    ref="video"
                    autoplay
                    playsinline
                    muted
                    height="540px"
                    width="960px"
                    :srcObject.prop="depthStream"
                  >
                  </video>                   
                </v-col>
                <v-col align="center" v-else>
                  <v-img :src="dialogValues.stepperData.sampleImage2" alt=""
                      contain
                      height="540px"
                      width="960px">
                  </v-img>
                </v-col>
              </v-row>
            </v-card>
					</v-stepper-content>
					<v-stepper-content step="4">
						<v-card flat class="pa-4">
							<div class="d-flex align-center text-h5 mb-3 h-4" style="height:100%">
                <v-btn class="ml-2 white--text" @click="cancelEntryChanges()" color="red" fab>
									<v-icon>
                    mdi-close
                  </v-icon>
								</v-btn>
                <v-spacer />
									Review
								<v-spacer />
							</div>
              <v-divider/>
              <v-row align-content-center>
                <v-col align="center">
                  <b>Badge 1</b>
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  <b>Badge 2</b>
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  <b>Badge 3</b>
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  <b>Badge 4</b>
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  <b>Badge 5</b>
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  <b>Badge 6</b>
                </v-col>
              </v-row>
              <v-divider/>
							<v-row>
                <v-col align="center">
                  {{ dialogValues.stepperData.tray }}
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  {{ dialogValues.stepperData.badge2 }}
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  {{ dialogValues.stepperData.badge3 }}
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  {{ dialogValues.stepperData.badge4 }}
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  {{ dialogValues.stepperData.badge5 }}
                </v-col>
                <v-divider
                  vertical
                />
                <v-col align="center">
                  {{ dialogValues.stepperData.badge6 }}
                </v-col>
              </v-row>
              <v-divider/>
              <v-row>
                <v-col align="center">
                  <b>Top Light Image</b>
                </v-col>
                <v-col align="center">
                  <b>Bottom Light Image</b>
                </v-col>
              </v-row>
              <v-divider/>
              <v-row>
                <v-col cols="6" align="center">
                  <v-img :src="dialogValues.stepperData.sampleImage1" alt=""
                      contain
                      height="540px"
                      width="960px">
                  </v-img>
                </v-col>
                <v-divider vertical/>
                <v-col cols="6" align="center">
                  <v-img :src="dialogValues.stepperData.sampleImage2" alt=""
                      contain
                      height="540px"
                      width="960px">
                  </v-img>
                </v-col>
              </v-row>
						</v-card>
					</v-stepper-content>
				</v-stepper-items>
        <!-- <v-stepper-actions> <- use this when upgrading to Vue3 -->
        <div>
          <v-divider/>
          <v-row class="pa-4">
            <v-btn :color="'secondary'"
              v-if="dialogValues.step > 1"
              @click="dialogValues.step -= 1">
              Previous
            </v-btn>
            <v-spacer />
            <v-btn :color="'primary'"
              v-if="(dialogValues.step == 2 || dialogValues.step == 3) && !currPictureTaken"
              @click="capture()">
              <v-icon>
                mdi-camera
              </v-icon>
            </v-btn>
            <v-btn :color="'primary'"
              v-if="(dialogValues.step == 2 || dialogValues.step == 3) && currPictureTaken"
              @click="resetPicture()">
              <v-icon>
                mdi-refresh
              </v-icon>
            </v-btn>
            <v-spacer />
            <v-btn :color="'success'"
              v-if="dialogValues.step < 4"
              :disabled="!currPictureTaken || dialogValues.stepperData.tray == null || dialogValues.stepperData.tray === ''"
              @click="dialogValues.step += 1">
              Next
            </v-btn>
            <v-btn
              v-if="dialogValues.step === 4"
              :color="'success'"
                  @click="saveTray()">
                  Save
            </v-btn>
          </v-row>
        </div>
        <!-- </v-stepper-actions> -->
			</v-stepper>
      <v-overlay :value="isBusy">
      <v-row>
        <span class="text-h4" color="overlaytext">
          {{ overlayMessage }}
        </span>
      </v-row>
      <v-row>
        <v-progress-linear
          model-value="40"
          color="primary"
          label-position="bottom"
          hide-value
          indeterminate>
        </v-progress-linear>
      </v-row>
    </v-overlay>
	</v-dialog>
    <v-dialog v-model="dialogValues.showDeleteDialog" persistent max-width="250">
      <v-card color="cardbackground">
        <v-card-title class="text-h5">Confirm Delete</v-card-title>
        <v-card-text>Is it okay to delete <br />
          Tray: <b>{{ entryToDelete.tray }}</b>?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" class="white--text" @click="deleteTrayEntry()">Yes</v-btn>
          <v-btn color="secondary" @click="dialogValues.showDeleteDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-overlay :value="isBusy">
      <v-row>
        <span class="text-h4" color="overlaytext">
          {{ overlayMessage }}
        </span>
      </v-row>
      <v-row>
        <v-progress-linear
          model-value="40"
          color="primary"
          label-position="bottom"
          hide-value
          indeterminate>
        </v-progress-linear>
      </v-row>
    </v-overlay>
  </div>
</template>
<script>
import OfflineStorage from '../utils/offlineStorage';
import RealSenseCameraApi from '../api/realSenseCameraApi';
import { mapState } from 'vuex'

export default {
  data() {
    return {
      currentTrays: [],
      dialogValues: {
        editMode: false,
        step: 1,
        stepperData: {},
        showAddDialog: false,
        showDeleteDialog: false,
        showUnknownEggDialog: false
      },
      manualTrayOverride: false,
      camera: new RealSenseCameraApi(),
      cameraConnected: false,
      cameraMessage: "",
      cameraStreaming: false,
      rtcPeerConnection: new RTCPeerConnection({}),
      colorVideo: true,
      colorStream: null,
      depthStream: null,
      isBusy: false,
      overlayMessage: "",
      waitingTrack1: true,
      visionGroups: null,
      visionGroup: {},
      entryToDelete: {},
      warningMsgDisplay: false,
      warningMsg: "",
      errorBadgeMsg: "",
      errorGradeMsg: "",
      errorConfirmBadgeMsg: "",
      windowSize: { x: 0, y: 0 }
    }
  },

  mounted() {
    this.onResize();
    this.$refs.mainDiv.scrollIntoView();
    //setTimeout(() => this.focusBadge(), 200);
  },

  unMounted() {
    this.disconnectCamera();
  },

  async created() {
    try {
      if (this.isBusy) return;
      this.isBusy = true;
      this.overlayMessage = "Loading Vision Group";
      this.visionGroups = await OfflineStorage.getItem('eggVisionGroups');
      const eggVisionGroup = this.visionGroups.find(x => x.groupName === this.groupName);

      this.visionGroup = eggVisionGroup;
      this.currentTrays = eggVisionGroup.trays;

    } catch (error) {
      this.$root.showNoticeError(error.message);
    }
    finally {
      this.isBusy = false;
    }
  },

  computed: {
    ...mapState(['settings']),
    badges() {
      const nameList = this.groupName.split("_");
      const farm = nameList[0];
      const line = nameList[1];
      const mg = parseInt(nameList[2]);
      return this.settings.badges.filter(b => b.farmCode === farm
        && b.line === line
        && b.miniGenerationNum == mg
        && b.isPedigree
      ).sort((a, b) => {
        let noLetterA = a.badgeCode[0].toLowerCase() === a.badgeCode[0].toUpperCase();
        let noLetterB = b.badgeCode[0].toLowerCase() === b.badgeCode[0].toUpperCase();
        if(noLetterA || noLetterB) {
          if(noLetterA && noLetterB) {
            return parseInt(a.badgeCode) - parseInt(b.badgeCode);
          }
          else if(noLetterA) return -1;
          else return 1;
        }
        
        let numA = parseInt(a.badgeCode.substring(1));
        let numB = parseInt(b.badgeCode.substring(1));
        return numA - numB;
        });
    },
    referenceBadges() {
      const nameList = this.groupName.split("_");
      const line = nameList[1];
      const mg = parseInt(nameList[2]);
      return this.settings.referenceBadges.filter(b => b.line === line && b.miniGenerationNum === mg)
        .sort((a, b) => {
          let noLetterA = a.badgeCode[0].toLowerCase() === a.badgeCode[0].toUpperCase();
          let noLetterB = b.badgeCode[0].toLowerCase() === b.badgeCode[0].toUpperCase();
          if(noLetterA || noLetterB) {
            if(noLetterA && noLetterB) {
              return parseInt(a.badgeCode) - parseInt(b.badgeCode);
            }
            else if(noLetterA) return -1;
            else return 1;
          }
          
          let numA = parseInt(a.badgeCode.substring(1));
          let numB = parseInt(b.badgeCode.substring(1));
          return numA - numB;
        });
    },
    trays() {
      var traySet = new Set(this.currentTrays.map(t => t.tray));
      let list = this.referenceBadges
        .map((b, i) => {if(i == 0 || i % 6 == 0) return b.badgeCode;})
        .filter((b) => b != undefined && !traySet.has(b));
      return list;
    },
    breakpoint() {
      return this.$vuetify.breakpoint.name;
    },
    captureButtonDisabled(){
      return !this.cameraStreaming;
    },
    isDialogOpen() {
      return this.showConfigDialog
        || this.dialogValues.showAddDialog
        || this.dialogValues.showDeleteDialog
        || this.dialogValues.showUnknownEggDialog;
    },
    groupName() {
      return this.$route.params.groupName;
    },
    imageCols() {
      return this.smallerScreen ? 12 : 6
    },
    image1Taken() {
      return this.dialogValues.stepperData.image1Data.length > 0
    },
    image2Taken(){
      return this.dialogValues.stepperData.image2Data.length > 0
    },
    currPictureTaken() { 
      switch(this.dialogValues.step) {
        case 2:
          return this.image1Taken;
        case 3:
          return this.image2Taken;
      }
      return true;
    },
    smallerScreen() {
      return this.breakpoint === 'xs' || this.breakpoint === 'sm';
    },
    tblHeaders() {
      const tableCols = [
        { text: 'Tray', value: 'tray', align: 'center' },
        { text: 'Image 1', value: 'image1', align: 'center' },
        { text: 'Image 2', value: 'image2', align: 'center' },
        { text: 'Date', value: 'date', align: 'center' },
        { text: 'Edit', value: 'edit', align: 'center'},
        { text: 'Delete', value: 'delete', align: 'center' }
      ];
      return tableCols;
    },
    tableHeight() {
      let tableSize = this.windowSize.y - 395;

      if (this.smallerScreen) {
        tableSize += 4; // v-app-bar shorter for xs and sm
      }
      if (tableSize < 200) tableSize = 200;
      return tableSize;
    }
  },

  methods: {
    async cancelEntryChanges() {
      if(this.dialogValues.stepperData.sampleImage1 !== null && this.dialogValues.editMode === false)
        await this.deleteFiles(this.dialogValues.stepperData.base_file_name1);
      if(this.dialogValues.stepperData.sampleImage2 !== null && this.dialogValues.editMode === false)
        await this.deleteFiles(this.dialogValues.stepperData.base_file_name2)
      this.dialogValues.editMode = false;
      this.dialogValues.showAddDialog = false;
      this.initializeStepperData();
    },
    async saveTray() {
      try {
        if (this.isBusy) return;
        this.loadWingbands();
        this.isBusy = true;
        this.overlayMessage = "Saving data.";
        const now = new Date().toISOString();
        
        const newTray = {...this.dialogValues.stepperData};
        newTray.date = now;
        let existingTrayIndex = this.currentTrays.findIndex(b => b.tray === newTray.tray);

        if(existingTrayIndex === -1)
          this.currentTrays.unshift({...newTray}); //add to beginning
        else
          this.currentTrays[existingTrayIndex] = {...newTray};

        const eggVisionGroup = this.visionGroups.find(x => x.groupName === this.groupName);
        eggVisionGroup.trays = this.currentTrays;
        this.dialogValues.editMode = false;
        await OfflineStorage.setItem('eggVisionGroups', this.visionGroups);
        this.$root.showNoticeSuccess('Data added to group.')
      }
      catch (e) {
        this.$root.showNoticeError(e.message)
      }
      finally {
        this.dialogValues.showAddDialog = false;
        this.initializeStepperData();
        this.isBusy = false;
      }
    },
    startStepper() {
      this.initializeStepperData();
      this.manualTrayOverride = false;
      this.dialogValues.showAddDialog = true;
      setTimeout(() => this.focusTray(), 200);
    },
    confirmTrayValue() {
      let trayIndex = this.referenceBadges
        .findIndex((b) => b.badgeCode === this.dialogValues.stepperData.tray)
      this.dialogValues.stepperData.badge2 = trayIndex + 1 < this.referenceBadges.length
        ? this.referenceBadges[trayIndex + 1].badgeCode
        : null;
      this.dialogValues.stepperData.badge3 = trayIndex + 2 < this.referenceBadges.length
        ? this.referenceBadges[trayIndex + 2].badgeCode
        : null;
      this.dialogValues.stepperData.badge4 = trayIndex + 3 < this.referenceBadges.length
        ? this.referenceBadges[trayIndex + 3].badgeCode
        : null;
      this.dialogValues.stepperData.badge5 = trayIndex + 4 < this.referenceBadges.length
        ? this.referenceBadges[trayIndex + 4].badgeCode
        : null;
      this.dialogValues.stepperData.badge6 = trayIndex + 5 < this.referenceBadges.length
        ? this.referenceBadges[trayIndex + 5].badgeCode
        : null;
    },
    loadWingbands() {
      let badgeList = [];
      badgeList.push(this.dialogValues.stepperData.tray);
      badgeList.push(this.dialogValues.stepperData.badge2);
      badgeList.push(this.dialogValues.stepperData.badge3);
      badgeList.push(this.dialogValues.stepperData.badge4);
      badgeList.push(this.dialogValues.stepperData.badge5);
      badgeList.push(this.dialogValues.stepperData.badge6);
      let badgeWingbands = this.badges
      .map((b) => {
        if(badgeList.includes(b.badgeCode))
          return b})
      .filter((b) => b !== undefined);
      this.dialogValues.stepperData.wingbands = [];
      for (let i = 0; i < badgeList.length; i++) {
        let wingBand = badgeWingbands.find(b => b.badgeCode === badgeList[i]);
        if(wingBand)
          this.dialogValues.stepperData.wingbands.push(wingBand.wingband);
        else
          this.dialogValues.stepperData.wingbands.push(null);
      }
    },
    async resetPicture(){
      if(this.isBusy) return;
      this.isBusy = true;
      try {
        switch(this.dialogValues.step) {
          case 2:
            this.overlayMessage = `Deleting old pictures for ${this.dialogValues.stepperData.base_file_name1}`; 
            await this.camera.deleteFiles(this.dialogValues.stepperData.base_file_name1);
            this.dialogValues.stepperData.base_file_name1 = null;
            this.dialogValues.stepperData.image1Data = [];
            this.dialogValues.stepperData.sampleImage1 = null;
            break;
          case 3:
            this.overlayMessage = `Deleting old pictures for ${this.dialogValues.stepperData.base_file_name2}`; 
            await this.camera.deleteFiles(this.dialogValues.stepperData.base_file_name2);
            this.dialogValues.stepperData.base_file_name2 = null;
            this.dialogValues.stepperData.image2Data = [];
            this.dialogValues.stepperData.sampleImage2 = null;
            break;
        }
      }
      catch(e) {
          this.$root.showNoticeError(`Unable to delete old picture: ${e.message}`)
        }
        finally {
          this.isBusy = false;
        }
    },
    async connectCamera(){
      try {
        if (this.isBusy) return;
        this.isBusy = true;
        this.overlayMessage = "Initializing Camera.";
        this.cameraConnected = await this.camera.init();
        if(!this.cameraConnected)
        {
          this.cameraMessage = "Unable to connect to camera. Check that the camera is connected.";
          return;
        }

        this.overlayMessage = "Loading sensor preset.";
        await this.camera.loadSensorPreset("depth", "Custom");
        this.cameraMessage = "Connected to " + this.camera.selectedDevice.name;

        this.overlayMessage = "Saving device name.";
        let deviceName = await this.camera.getDeviceName();
        this.visionGroup.deviceName = deviceName;
        await OfflineStorage.setItem('eggVisionGroups', this.visionGroups);
      }
      catch(e) {
        this.$root.showNoticeError(`There was an error establishing a connection to the camera: ${e}`)
      }
      finally {
        this.isBusy = false;
      }
    },
    async disconnectCamera(){
      if(this.isBusy) return;
      await this.stopStreaming();
      try{
        this.isBusy = true;
        this.overlayMessage = "Disconnecting camera.";
        await this.camera.dereference();
      }
      catch(e){
        this.$root.showNoticeError(`There was an error disconnecting the camera: ${e.message}`)
      }
      finally{
        this.cameraConnected = false;
        this.isBusy = false;
      }
    },
    async startStreaming(){
      if(!this.cameraConnected || this.isBusy)
            return;
      this.isBusy = true;
      try{
        this.overlayMessage = "Setting post processing filters.";
        await this.camera.setFilterConfiguration("EggVision1");

        this.overlayMessage = "Starting the stream.";
        await this.camera.startStream("color", "depth");

        this.rtcPeerConnection.ontrack = (event) => {
          console.log("Update video stream");
          if(this.waitingTrack1) {
            this.colorStream = new MediaStream([event.track]);
            this.waitingTrack1 = false;
          } 
          else 
          {
            this.depthStream = new MediaStream([event.track]);
            this.waitingTrack1 = true;
          }
        }

        this.overlayMessage = "Completing handshake with the camera.";
        let offer = await this.camera.getRtcOffer("color", "depth");
        await this.rtcPeerConnection.setRemoteDescription(new RTCSessionDescription({sdp: offer.sdp, type: offer.type}));
        let myAnswer = await this.rtcPeerConnection.createAnswer();
        await this.rtcPeerConnection.setLocalDescription(myAnswer);
        myAnswer.session_id = offer.session_id;
        await this.camera.getRtcAnswer(myAnswer);
        this.cameraStreaming = true;
      }
      catch(e){
        this.$root.showNoticeError(`Unable to start stream: ${e.message}`)
      }
      finally{
        this.isBusy = false;
      }
    },
    async stopStreaming(){
      try{
        if(!this.cameraConnected || this.isBusy)
            return;
        this.isBusy = true;
        this.colorStream = null;
        this.depthStream = null;
        this.overlayMessage = "Severing connection with the stream.";
        await this.camera.closeRtcSession();
        this.rtcPeerConnection.close();
        this.rtcPeerConnection = new RTCPeerConnection();
        this.overlayMessage = "Closing stream.";
        await this.camera.stopStream();
      }
      catch(e){
        this.$root.showNoticeError(`Unable to stop stream: ${e.message}`)
      }
      finally{
        this.cameraStreaming = false;
        this.isBusy = false;
      }
    },
    async calibrate(){
        if(!this.cameraConnected || this.isBusy)
            return;
        this.isBusy = true;
        try{
          this.overlayMessage = "Loading advanced settings.";
          await this.camera.loadSettings();
          this.overlayMessage = "Calibrating camera.";
          let result = await this.camera.calibrate();
          this.$root.showNoticeSuccess(`Calibrate successful. New Distance Threshold: ${result}`);
        }
        catch(e){
          this.$root.showNoticeError(`Calibrate failed. ${e.message}`);
        }
        finally{
          this.isBusy = false;
        }
    },
    async capture(){
      if(!this.cameraStreaming || this.isBusy)
        return;
      this.isBusy = true;
      try{
        const date = new Date();
        const day = date.getDate().toString().padStart(2,"0");
        const month = (date.getMonth() + 1).toString().padStart(2,"0");
        const year = date.getFullYear();
        const hhmmss = date.getHours().toString().padStart(2,"0").concat(
          date.getMinutes().toString().padStart(2,"0"), 
          date.getSeconds().toString().padStart(2,"0"));
        const dateparts = [
              year,
              month,
              day,
              hhmmss
            ];
        let dateStr = dateparts.join('_');
        switch(this.dialogValues.step){
          case 2:
            {
              this.dialogValues.stepperData.date1 = dateStr;
              let fileparts1 = [
                this.visionGroup.farm, 
                this.visionGroup.line, 
                this.visionGroup.mg, 
                this.dialogValues.stepperData.tray, 
                "Set", 
                "1",
                dateStr
              ];
              this.dialogValues.stepperData.base_file_name1 = fileparts1.join('_');
              this.overlayMessage = `Capturing image for files: ${this.dialogValues.stepperData.base_file_name1}`;
              let results1 = await this.camera.capture(this.dialogValues.stepperData.base_file_name1)
              this.dialogValues.stepperData.image1Data = results1.Data;
              const dataUri = "data:image/png;base64," + results1.Image;
              this.$set(this.dialogValues.stepperData, 'sampleImage1', dataUri);
              break;
            }
          case 3:
            {
              let fileparts2 = [
                this.visionGroup.farm, 
                this.visionGroup.line, 
                this.visionGroup.mg,
                this.dialogValues.stepperData.tray, 
                "Set", 
                "2",
                dateStr
              ];
              this.dialogValues.stepperData.date2 = dateStr;
              this.dialogValues.stepperData.base_file_name2 = fileparts2.join('_');
              this.overlayMessage = `Capturing image for files: ${this.dialogValues.stepperData.base_file_name2}`;
              let results2 = await this.camera.capture(this.dialogValues.stepperData.base_file_name2)
              this.dialogValues.stepperData.image2Data = results2.Data;
              const dataUri = "data:image/png;base64," + results2.Image;
              this.$set(this.dialogValues.stepperData, 'sampleImage2', dataUri);
              break;
            }
        }
      }
      catch(e){
        this.$root.showNoticeError(`Image capture failed. ${e.message}`);
      }
      finally{
        this.isBusy = false;
      }
    },
    async deleteFiles(base_file_name){
      if(this.isBusy)
        return
      this.isBusy = true;
      try{
        this.overlayMessage = `Deleting files for ${base_file_name}`
        await this.camera.deleteFiles(base_file_name);
      }
      catch(e){
        this.$root.showNoticeError(`Images did not get deleted. ${e.message}`);
      }
      finally{
        this.isBusy = false;
      }
    },
    streamTypeTogle(){
      this.colorVideo = !this.colorVideo;
    },
    initializeStepperData(){
      this.dialogValues.stepperData = 
      {
        tray: null,
        badge2: null,
        badge3: null,
        badge4: null,
        badge5: null,
        badge6: null,
        wingbands: [null,null,null,null,null,null],
        date1: null,
        date2: null,
        image1Data: [],
        image2Data: [],
        sampleImage1: null,
        sampleImage2: null
      }
      this.dialogValues.step = 1;
    },
    newImageData(){
      return new
      {
        Dam: null,
        Egg: null,
        Area: null,
        Height: null,
        MinD: null,
        MedD: null,
        MaxD: null,
        Breadth: null,
        Width: null,
        Length: null,
        Circularity: null,
        Volume: null,
        Weight: null,
        l: null,
        a: null,
        b: null,
        lStd: null,
        aStd: null,
        bStd: null
      }
    },
    selectItem(event, row) {
      row.select();
    },
    clearSelectedItem(data, row) {
      if (row.isSelected && this.isDialogOpen == false) {
        row.select(false);
      }
    },
    confirmDeleteTrayEntry(item) {
      this.entryToDelete = {
        tray: item.tray,
        date: item.date
      }
      this.dialogValues.showDeleteDialog = true;
    },
    editTrayEntry(editItem) {
      const index = this.currentTrays.findIndex(x =>
        x.tray === editItem.tray
      );
      let currentTray = this.currentTrays[index];

      this.dialogValues.step = 1;
      this.dialogValues.stepperData = {...currentTray};
      this.manualTrayOverride = false;
      this.dialogValues.editMode = true;
      this.dialogValues.showAddDialog = true;
    },
    async deleteTrayEntry() {
      if(this.isBusy) return;
      this.isBusy = true;
      
      try {
        this.dialogValues.showDeleteDialog = false;
        const index = this.currentTrays.findIndex(x =>
          x.tray === this.entryToDelete.tray &&
          x.date === this.entryToDelete.date
        );

        this.overlayMessage = `Deleting images for Tray: ${this.entryToDelete.tray} Date: ${this.entryToDelete.date}`;
        let trayToDelete = this.currentTrays[index];
        if(trayToDelete.base_file_name1)
          await this.camera.deleteFiles(trayToDelete.base_file_name1);
        if(trayToDelete.base_file_name2)
          await this.camera.deleteFiles(trayToDelete.base_file_name2);

        this.currentTrays.splice(index, 1);

        const eggVisionGroup = this.visionGroups.find(x => x.groupName === this.groupName);
        eggVisionGroup.trays = this.currentTrays;
        this.visionGroup = JSON.parse(JSON.stringify(eggVisionGroup));
        await OfflineStorage.setItem('eggVisionGroups', this.visionGroups);

        this.$root.showNoticeSuccess(`Tray: ${this.entryToDelete.tray} Date: ${this.entryToDelete.date} deleted successfully`);

        
      } catch (error) {
        this.$root.showNoticeError(error.message);
      }
      finally {
        this.isBusy = false;
      }
    },
    formatDate(dt) {
      let localDt = new Date(dt).toLocaleString();
      localDt = localDt.replace(',', '').replace(' ', '\n');
      return localDt;
    },
    focusTray() {
      //if (this.$refs.trayRef == null) return;
      //document.activeElement.blur();
      this.$refs.trayRef.focus();
      this.$refs.trayRef.activateMenu();
    },
    getLineBgColor(lineCode) {
      let line = this.settings.farmLines.find(fl => fl.lineCode == lineCode);
      if (line) {
        return `#${line.backgroundColor}`;
      } else {
        return '';
      }
    },
    getLineTextColor(lineCode) {
      let line = this.settings.farmLines.find(fl => fl.lineCode == lineCode);
      if (line) {
        return `#${line.textColor}`;
      } else {
        return '';
      }
    },
    makeErrorSound() {
      if (this.settings.isErrorSoundEnabled) {
        const audio = new Audio('sound/error-beep.mp3');
        audio.play();
      }
    },
    makeSuccessSound() {
      if (this.settings.isSuccessSoundEnabled) {
        const audio = new Audio('sound/success-chime.mp3');
        audio.play();
      }
    },
    makeWarningSound() {
      if (this.settings.isWarningSoundEnabled) {
        const audio = new Audio('sound/warning-beep.mp3');
        audio.play();
      }
    },
    makeBeepSound() {
      if (this.settings.isSuccessSoundEnabled) {
        const audio = new Audio('sound/success-beep.mp3');
        audio.play();
      }
    },
    navigateBack() {
      this.$router.replace({ name: 'Egg Vision Groups' });
    },
    onResize() {
      this.windowSize = { x: window.innerWidth, y: window.innerHeight }
    },
    resetErrorMessages() {
      this.errorBadgeMsg = "";
      this.errorGradeMsg = "";
      this.errorConfirmBadgeMsg = "";
    },
    resetInputFields() {
      setTimeout(() => { // needed for timing issue after closing error notice.
        this.resetErrorMessages();
      }, 100);
    },
    sticker() {
      if (this.config.dontStickerWarningDisabled == false) {
        if (this.badgeObj.fuzzyMiniGenerationNum <= -8 || this.badgeObj.dispositionValueCode == "Extra Pedigree") {
          return false;
        }
      }
      return true;
    },
    //stripBeforeNull(inputString) {
    //  if (inputString == null) return null;
    //  //* This method is used to strip the "[00]" from the end of the string.
    //  //* This is due to the RFID Reader translating null characters to "[00]".
    //  const match = inputString.match(/\[00\]/);
    //  if (match) {
    //    const index = match.index;
    //    return this.toUpperCase(inputString.slice(0, index))
    //  }
    //  return this.toUpperCase(inputString);
    // },
    toUpperCase(value) {
      return (value || "").toUpperCase();
    }
  }
}
</script>
<style>
.text-center {
  /* shrink table padding */
  padding: 0px !important;
  font-size: 12px !important;
}

.v-select__selection {
  color: var(--lineTextColor) !important
}

.v-data-footer {
  flex-wrap: nowrap;
  padding: 0 0 0 0
}

.overlay {
  position: fixed;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.5);
  z-index: 9999;
}
</style>